/**
 * Advanced-disclosure controls shared by agenda items and automations
 * (spec §7): Disable / Re-enable / Remove + inline server findings.
 * Findings come from the store's last validation (Task 5) — semantic
 * truth is server-side; `overlay_tombstone_dependency`'s message IS the
 * "why you can't disable this" explanation, rendered verbatim.
 */
import { useWorkflowConfigStore } from '@/stores/workflowConfigStore'
import { filterFindings } from '@/utils/workflowConfigView'
import { Badge } from '@/components/ui'

type KeyedSection = 'slots' | 'needs' | 'derived' | 'side_effects'

export function EntryControls({ section, itemKey, origin, disabled }: {
  section: KeyedSection
  itemKey: string
  origin: 'base' | 'added'
  disabled: boolean
}) {
  const advanced = useWorkflowConfigStore((s) => s.advanced)
  const disableEntry = useWorkflowConfigStore((s) => s.disableEntry)
  const enableEntry = useWorkflowConfigStore((s) => s.enableEntry)
  if (!advanced) return null

  const btn =
    'rounded-sm border border-border px-2 py-0.5 text-xs font-medium text-foreground hover:bg-muted'
  if (origin === 'added') {
    return (
      <button type="button" className={btn} aria-label={`remove ${itemKey}`}
        onClick={() => disableEntry(section, itemKey)}>
        Remove
      </button>
    )
  }
  return disabled ? (
    <button type="button" className={btn} aria-label={`re-enable ${itemKey}`}
      onClick={() => enableEntry(section, itemKey)}>
      Re-enable
    </button>
  ) : (
    <button type="button" className={btn} aria-label={`disable ${itemKey}`}
      onClick={() => disableEntry(section, itemKey)}>
      Disable
    </button>
  )
}

export function InlineFindings({ paths }: { paths: string[] }) {
  // Select the stored reference (stable), filter in-component — a
  // filtering selector would return a fresh array per snapshot (zustand
  // v5 getSnapshot rule, Global Constraints).
  const validation = useWorkflowConfigStore((s) => s.validation)
  const findings = validation ? filterFindings(validation.findings, paths) : []
  if (findings.length === 0) return null
  return (
    <ul className="text-xs text-destructive space-y-0.5">
      {findings.map((f, i) => (
        <li key={i}>{f.message}</li>
      ))}
    </ul>
  )
}

export function OriginBadge({ origin, disabled }: {
  origin?: 'base' | 'added'
  disabled?: boolean
}) {
  if (disabled) return <Badge variant="secondary">Disabled</Badge>
  if (origin === 'added') return <Badge variant="success">Added by you</Badge>
  return null
}
